/**
 * Lead-magnet definitions + pure lookups — the data layer.
 *
 * Split out from lead-magnets.ts (which adds the browser-only helpers) so the
 * Node upload script (scripts/upload-lead-magnets.ts) can read the magnet list
 * WITHOUT pulling in `import.meta.env` (via video-cdn) or `document` (via
 * triggerDownload) — neither typechecks under tsconfig.scripts.json. See
 * lead-magnets.ts for the full feature + serving overview.
 */

export interface LeadMagnet {
  /** Test-landing slug this magnet belongs to (e.g. "sat"). */
  slug: string;
  /**
   * URL-safe leading-slash basename (e.g. "/sat-playbook.pdf"). Doubles as the
   * same-origin dev path under apps/portal/public/ and the CDN key suffix; the
   * production URL is derived from it by leadMagnetHref() in lead-magnets.ts.
   */
  file: string;
  /** Suggested filename for the browser's "Save as". */
  downloadName: string;
  /** Display name, e.g. "Free SAT Playbook". Used in popup copy + toast. */
  title: string;
  /** Congrats-toast headline. */
  toastTitle: string;
  /** Congrats-toast body. */
  toastBody: string;
  /** Label on the toast's manual-download button (the no-popup-block fallback). */
  toastAction: string;
}

const LEAD_MAGNETS: Record<string, LeadMagnet> = {
  sat: {
    slug: "sat",
    file: "/sat-playbook.pdf",
    downloadName: "SAT Playbook.pdf",
    title: "Free SAT Playbook",
    toastTitle: "You got the Free SAT Playbook!",
    toastBody: "Your download is starting. Didn't see it? Grab it below.",
    toastAction: "Download the SAT Playbook",
  },
  act: {
    slug: "act",
    file: "/act-playbook.pdf",
    downloadName: "ACT Playbook.pdf",
    title: "Free ACT Playbook",
    toastTitle: "You got the Free ACT Playbook!",
    toastBody: "Your download is starting. Didn't see it? Grab it below.",
    toastAction: "Download the ACT Playbook",
  },
  "sc-ready": {
    slug: "sc-ready",
    file: "/sc-ready-playbook.pdf",
    downloadName: "SC Ready Playbook.pdf",
    title: "Free SC READY Playbook",
    toastTitle: "You got the Free SC READY Playbook!",
    toastBody: "Your download is starting. Didn't see it? Grab it below.",
    toastAction: "Download the SC READY Playbook",
  },
  caaspp: {
    slug: "caaspp",
    file: "/caaspp-playbook.pdf",
    downloadName: "CAASPP Playbook.pdf",
    title: "Free CAASPP Playbook",
    toastTitle: "You got the Free CAASPP Playbook!",
    toastBody: "Your download is starting. Didn't see it? Grab it below.",
    toastAction: "Download the CAASPP Playbook",
  },
  "nc-eog-eoc": {
    slug: "nc-eog-eoc",
    file: "/nc-eog-eoc-playbook.pdf",
    // Saved file uses the short "NC EOG" form; on-page title keeps "EOG/EOC".
    downloadName: "NC EOG Playbook.pdf",
    title: "Free NC EOG/EOC Playbook",
    toastTitle: "You got the Free NC EOG/EOC Playbook!",
    toastBody: "Your download is starting. Didn't see it? Grab it below.",
    toastAction: "Download the NC EOG/EOC Playbook",
  },
};

/** Every configured magnet — the upload script's single source of truth. */
export const LEAD_MAGNETS_LIST: LeadMagnet[] = Object.values(LEAD_MAGNETS);

/** S3/CloudFront key prefix the upload script writes lead-magnet PDFs under. */
export const LEAD_MAGNET_CDN_PREFIX = "lead-magnets";

/** The magnet for a funnel slug, or null when that funnel has no magnet. */
export function leadMagnetForSlug(slug: string | null | undefined): LeadMagnet | null {
  if (!slug) return null;
  return LEAD_MAGNETS[slug] ?? null;
}
